// Payment-time card effects: resource substitution and fortress-card
// discounts, derived (never stored) from a player's implemented cards.
//
// Costs are `[minerals, food, vigor, obsidian]` tuples, or the length-1
// wildcard `[n]` ("n of any resource"). `paymentPlan` turns a cost into the
// concrete per-resource deduction under the player's substitutions — the ONE
// affordability answer both the server (validate + spend) and the client
// (button/target gating) use, so an enabled control always passes validation.
import { RESOURCES } from "../types/resource.js";
import { hasPassive } from "./registry.js";
const MINERALS = 0;
const FOOD = 1;
const VIGOR = 2;
const OBSIDIAN = 3;
export const NO_SUBSTITUTIONS = Object.freeze({
    obsidianWild: false,
    foodVigorInterchange: false,
});
/** The substitution rules a player's implemented cards grant right now. */
export function substitutionsFor(cards, effectOf) {
    let obsidianWild = false;
    let foodVigorInterchange = false;
    for (const fc of cards) {
        const fx = effectOf(fc.cardId);
        if (!fx)
            continue;
        if (hasPassive(fx, "obsidian_wildcard"))
            obsidianWild = true;
        if (hasPassive(fx, "food_energy_interchange"))
            foodVigorInterchange = true;
    }
    return { obsidianWild, foodVigorInterchange };
}
/**
 * The concrete per-resource deduction paying `cost` from `bank` under `subs`,
 * or null when unaffordable. Deterministic and greedy, favouring the least
 * flexible money first: each component pays from its own resource, a food /
 * vigor shortfall then draws on its partner (interchange), and whatever is
 * still short falls to leftover obsidian (wildcard). A length-1 wildcard cost
 * drains resources in canonical order, exactly like the base `spend`.
 */
export function paymentPlan(bank, cost, subs) {
    const pay = [0, 0, 0, 0];
    if (cost.length === 1) {
        let need = cost[0] ?? 0;
        if (bank.reduce((s, n) => s + n, 0) < need)
            return null;
        for (let i = 0; i < 4 && need > 0; i += 1) {
            const take = Math.min(bank[i] ?? 0, need);
            pay[i] = take;
            need -= take;
        }
        return pay;
    }
    const short = [0, 0, 0, 0];
    for (let i = 0; i < 4; i += 1) {
        const want = cost[i] ?? 0;
        const own = Math.min(bank[i] ?? 0, want);
        pay[i] = own;
        short[i] = want - own;
    }
    if (subs.foodVigorInterchange) {
        // Only one of the pair can be short after paying from itself.
        const spareVigor = (bank[VIGOR] ?? 0) - pay[VIGOR];
        const coverFood = Math.min(short[FOOD], spareVigor);
        pay[VIGOR] += coverFood;
        short[FOOD] -= coverFood;
        const spareFood = (bank[FOOD] ?? 0) - pay[FOOD];
        const coverVigor = Math.min(short[VIGOR], spareFood);
        pay[FOOD] += coverVigor;
        short[VIGOR] -= coverVigor;
    }
    if (subs.obsidianWild) {
        for (const i of [MINERALS, FOOD, VIGOR]) {
            const spare = (bank[OBSIDIAN] ?? 0) - pay[OBSIDIAN];
            const cover = Math.min(short[i], spare);
            pay[OBSIDIAN] += cover;
            short[i] -= cover;
        }
    }
    return short.every((s) => s === 0) ? pay : null;
}
/**
 * Pin a wildcard cost (`[n]`, "n of any resource") to the payer's chosen
 * resource: the concrete four-slot tuple paying all n from `resource`. A fixed
 * cost, or a missing/unknown choice, passes through unchanged — an unpinned
 * wildcard falls back to `paymentPlan`'s canonical-order drain.
 */
export function wildcardCostAs(cost, resource) {
    if (cost.length !== 1 || resource === undefined)
        return cost;
    const idx = RESOURCES.indexOf(resource);
    if (idx < 0)
        return cost;
    const concrete = [0, 0, 0, 0];
    concrete[idx] = cost[0] ?? 0;
    return concrete;
}
/** True when `cost` is payable from `bank` under `subs`. */
export function canAffordWith(bank, cost, subs) {
    return paymentPlan(bank, cost, subs) !== null;
}
/**
 * The Obsidian cost `player` actually pays to implement `card`, after any
 * fortress-cost discounts their implemented cards grant (Inverse Molehill).
 * `implementedFortresses` is the game's cumulative implemented-fortress count
 * (`GameState.fortressesImplemented`) that a `perImplementedFortress`
 * printing discounts by — callers without it get no such discount.
 * `playerCount` is the number of seated players a `costSplitByPlayers` card
 * (Horny Neighbour's "50/X") divides its printed total by — callers without
 * it see the undivided total.
 */
export function fortressCostFor(cards, card, effectOf, implementedFortresses = 0, playerCount = 1) {
    const cost = [...card.cost];
    if (effectOf(card.id)?.costSplitByPlayers) {
        cost[OBSIDIAN] = Math.ceil((cost[OBSIDIAN] ?? 0) / Math.max(1, playerCount));
    }
    for (const fc of cards) {
        const discount = effectOf(fc.cardId)?.fortressDiscount;
        if (!discount || discount.deck !== card.kind)
            continue;
        // An Inverse Molehill printing (catalogue "15"+) discounts by how many
        // fortresses have been implemented this game instead of a fixed amount.
        const amount = discount.perImplementedFortress
            ? implementedFortresses
            : discount.obsidian;
        cost[OBSIDIAN] = Math.max(discount.minObsidian, (cost[OBSIDIAN] ?? 0) - amount);
    }
    return cost;
}
//# sourceMappingURL=payment.js.map